import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../utils/axios";
import "./styles/applicationErrorDetails.css";

interface ErrorDetails {
  id: number;
  service_name: string;
  error_type: string;
  error_subtype: string;
  search_value: string;
  error_message: string;
  context_data: any;
  user_session_id: string;
  created_at: string;
  environment: string;
  http_status_code?: number;
  request_url?: string;
  request_method?: string;
  client_ip?: string;
  stack_trace?: string;
  resolved?: boolean;
  tags?: string;
  user_identity?: string;
  log_level?: string;
}

const ApplicationErrorDetails = () => {
  const { errorId } = useParams<{ errorId: string }>();
  const [details, setDetails] = useState<ErrorDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchDetails = () => {
    setLoading(true);
    api
      .get(`/admin/application-errors/${errorId}/details`)
      .then((res) => setDetails(res.data))
      .catch(() => setError("Failed to load error details."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line
  }, [errorId]);

  const handleExportJSON = () => {
    if (!details) return;
    const blob = new Blob([JSON.stringify(details, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `application_error_${details.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    if (!details) return;
    const fields = [
      "id",
      "service_name",
      "error_type",
      "error_subtype",
      "search_value",
      "error_message",
      "user_session_id",
      "created_at",
      "environment",
      "context_data"
    ];
    const csv =
      fields.join(",") +
      "\n" +
      fields
        .map((field) => {
          let value = (details as any)[field];
          if (typeof value === "object") value = JSON.stringify(value);
          if (typeof value === "string" && value.includes(",")) value = `"${value.replace(/"/g, '""')}"`;
          return value;
        })
        .join(",");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `application_error_${details.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSetResolved = async (resolved: boolean) => {
    if (!details) return;
    await api.post(`/admin/application-errors/${details.id}/resolve`, { resolved });
    setDetails({ ...details, resolved });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!details) return <div>No details found.</div>;

  return (
    <div className="admin-dashboard-wrapper">
      <header className="admin-header-bar">
        <div className="admin-header-logo">
          <div>
            <h1>Admin Portal</h1>
            <p>Manage client operations, uploads, and validations</p>
          </div>
        </div>
      </header>
      <div className="error-details-container">
        <nav style={{ marginBottom: 16, fontSize: 14 }}>
          <span className="breadcrumb-link" onClick={() => navigate('/admin-dashboard')}>Admin Dashboard</span>
          <span style={{ margin: '0 8px' }}>/</span>
          <span className="breadcrumb-link" onClick={() => navigate('/admin/application-errors')}>Application Errors</span>
          <span style={{ margin: '0 8px' }}>/</span>
          <span style={{ color: '#003da5' }}>Error #{details.id}</span>
        </nav>
        <div className="error-details-actions">
          <button onClick={() => navigate('/admin/application-errors')}>Back</button>
          <button onClick={fetchDetails}>Refresh</button>
          <button onClick={handleExportJSON}>Export as JSON</button>
          <button onClick={handleExportCSV}>Export as CSV</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <span className={details.resolved ? "resolved-badge" : "unresolved-badge"}>
            {details.resolved ? "Resolved" : "Unresolved"}
          </span>
          {details.resolved === false && (
            <button className="details-resolve-btn" onClick={() => handleSetResolved(true)}>Mark as Resolved</button>
          )}
          {details.resolved === true && (
            <button className="details-unresolve-btn" onClick={() => handleSetResolved(false)}>Mark as Unresolved</button>
          )}
        </div>
        <table className="error-details-table">
          <tbody>
            <tr><td>ID</td><td>{details.id}</td></tr>
            {details.service_name && <tr><td>Service</td><td>{details.service_name}</td></tr>}
            {details.error_type && <tr><td>Error Type</td><td>{details.error_type}</td></tr>}
            {details.error_subtype && <tr><td>Error Subtype</td><td>{details.error_subtype}</td></tr>}
            {details.search_value && <tr><td>Search Value</td><td>{details.search_value}</td></tr>}
            {details.error_message && <tr><td>Error Message</td><td>{details.error_message}</td></tr>}
            {details.user_session_id && <tr><td>User Session ID</td><td>{details.user_session_id}</td></tr>}
            {details.user_identity && <tr><td>User Identity</td><td>{details.user_identity}</td></tr>}
            {details.created_at && <tr><td>Created At</td><td>{details.created_at.replace('T', ' ').slice(0, 19)}</td></tr>}
            {details.environment && <tr><td>Environment</td><td>{details.environment}</td></tr>}
            {details.log_level && <tr><td>Log Level</td><td>{details.log_level}</td></tr>}
            {details.http_status_code !== undefined && details.http_status_code !== null && <tr><td>HTTP Status Code</td><td>{details.http_status_code}</td></tr>}
            {details.request_url && <tr><td>Request URL</td><td>{details.request_url}</td></tr>}
            {details.request_method && <tr><td>Request Method</td><td>{details.request_method}</td></tr>}
            {details.client_ip && <tr><td>Client IP</td><td>{details.client_ip}</td></tr>}
            {details.stack_trace && <tr><td>Stack Trace</td><td><div className="context-data-scroll"><pre>{details.stack_trace}</pre></div></td></tr>}
            {details.resolved !== undefined && details.resolved !== null && <tr><td>Resolved</td><td>{details.resolved ? 'Yes' : 'No'}</td></tr>}
            {details.tags && (
              <tr>
                <td>Tags</td>
                <td>
                  {details.tags.split(',').map((tag: string, idx: number) => (
                    <span key={idx} className="tag-badge">{tag.trim()}</span>
                  ))}
                </td>
              </tr>
            )}
            {details.context_data && <tr><td>Context Data</td><td><div className="context-data-scroll"><pre>{JSON.stringify(details.context_data, null, 2)}</pre></div></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApplicationErrorDetails;
